import { Link } from 'react-router-dom';
import { useFiscalSigningKeys } from '@/hooks/use-fiscal-signing-keys';
import { useT } from '@/i18n/use-t';

/**
 * PFC-13 — the Fiscal cabinet's warning about signing keys that expire within
 * the configured window (`warnDays`, 30 by default) or have expired. Shown on
 * every cabinet tab, above the tab's content; nothing when all keys are fine.
 */
export function SigningKeyExpiryBanner() {
  const t = useT('admin_ui');
  const { data } = useFiscalSigningKeys('current', true);
  const keys = (data?.items ?? []).filter((k) => k.status === 'expiring' || k.status === 'expired');
  if (keys.length === 0) return null;

  return (
    <div
      role="status"
      className="space-y-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300"
    >
      {keys.map((key) => (
        <p key={key.id}>
          {key.status === 'expired'
            ? t(
                'fiscalCabinet.signingKeys.expiry.expired',
                'The signing key of {entity} has expired. Upload a new key to keep signing.',
              ).replace('{entity}', key.legalEntity.name)
            : t(
                'fiscalCabinet.signingKeys.expiry.expiring',
                'The signing key of {entity} expires in {days} days ({date}). Upload a new key before then.',
              )
                .replace('{entity}', key.legalEntity.name)
                .replace('{days}', String(Math.max(0, key.daysLeft)))
                .replace('{date}', key.expiresAt.slice(0, 10))}
        </p>
      ))}
      <Link to="/fiscal-cabinet/signing-keys" className="text-xs font-medium underline">
        {t('fiscalCabinet.signingKeys.expiry.link', 'Go to signing keys')}
      </Link>
    </div>
  );
}
